import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  ListItemIcon,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { useAuth } from '../../context/AuthContext';
import { groupMenuItems, menuItems } from '../../constants/menuItems';
import { useThemeMode, ThemeModePreference } from '../../context/ThemeModeContext';
import SideNav, { SIDE_NAV_RAIL_WIDTH, SIDE_NAV_WIDTH } from './SideNav';

const NAV_COLLAPSED_KEY = 'navCollapsed';

const readCollapsed = (): boolean => {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const Layout: React.FC = () => {
  const { logout, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { mode, resolvedMode, setMode } = useThemeMode();
  const [themeMenuAnchor, setThemeMenuAnchor] = useState<null | HTMLElement>(null);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const toggleCollapsed = () => {
    setCollapsed((previous) => {
      const next = !previous;
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, String(next));
      } catch {
        // Storage can be unavailable (private window); the choice just isn't remembered.
      }
      return next;
    });
  };

  const openThemeMenu = (event: React.MouseEvent<HTMLElement>) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const closeThemeMenu = () => {
    setThemeMenuAnchor(null);
  };

  const handleSelectThemeMode = (newMode: ThemeModePreference) => {
    setMode(newMode);
    closeThemeMenu();
  };

  const ThemeModeIcon = mode === 'system' ? SettingsBrightnessIcon : resolvedMode === 'dark' ? DarkModeIcon : LightModeIcon;

  const menuGroups = groupMenuItems(menuItems.filter((item) => hasRole(item.roles)));

  const barButtonSx = {
    color: 'inherit',
    border: '1px solid',
    borderColor: 'rgba(255, 255, 255, 0.5)',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.8)',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Box
        className="navigation-bar-wrapper"
        sx={{
          bgcolor: 'primary.main',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          pt: { xs: 1, sm: 1.5 },
          pb: { xs: 0.5, sm: 1 },
          borderRadius: 0,
          flexShrink: 0,
        }}
      >
        <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'transparent', boxShadow: 'none', zIndex: 1100, borderRadius: 0 }}>
          <Toolbar sx={{ px: 0, py: 0, minHeight: 'auto', borderRadius: 0 }}>
            {isMobile && (
              <IconButton color="inherit" edge="start" onClick={toggleDrawer} aria-label="Open menu" sx={{ mr: 2 }}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <IconButton onClick={openThemeMenu} aria-label="Theme settings" sx={barButtonSx}>
                <ThemeModeIcon />
              </IconButton>
              <Menu anchorEl={themeMenuAnchor} open={Boolean(themeMenuAnchor)} onClose={closeThemeMenu}>
                <MenuItem selected={mode === 'light'} onClick={() => handleSelectThemeMode('light')}>
                  <ListItemIcon>
                    <LightModeIcon fontSize="small" />
                  </ListItemIcon>
                  Light
                </MenuItem>
                <MenuItem selected={mode === 'dark'} onClick={() => handleSelectThemeMode('dark')}>
                  <ListItemIcon>
                    <DarkModeIcon fontSize="small" />
                  </ListItemIcon>
                  Dark
                </MenuItem>
                <MenuItem selected={mode === 'system'} onClick={() => handleSelectThemeMode('system')}>
                  <ListItemIcon>
                    <SettingsBrightnessIcon fontSize="small" />
                  </ListItemIcon>
                  System
                </MenuItem>
              </Menu>
              {!isMobile ? (
                <Button
                  variant="outlined"
                  onClick={handleLogout}
                  startIcon={<LogoutIcon />}
                  sx={{ ...barButtonSx, borderRadius: 0 }}
                >
                  Logout
                </Button>
              ) : (
                <IconButton onClick={handleLogout} aria-label="Logout" sx={barButtonSx}>
                  <LogoutIcon />
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {!isMobile && (
          <Box
            component="aside"
            sx={{
              width: collapsed ? SIDE_NAV_RAIL_WIDTH : SIDE_NAV_WIDTH,
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
              overflow: 'hidden',
            }}
          >
            <SideNav groups={menuGroups} isActive={isActive} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              bgcolor: 'background.default',
              overflowY: 'auto',
              overflowX: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Box sx={{ flexGrow: 1, pb: 2 }}>
              <Outlet />
            </Box>
          </Box>

          <Box
            component="footer"
            sx={{
              py: 3,
              px: 2,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              © 2024 E2E C.E.P. All rights reserved.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Drawer anchor="left" open={isMobile && drawerOpen} onClose={toggleDrawer} PaperProps={{ sx: { width: 280 } }}>
        <SideNav groups={menuGroups} isActive={isActive} onNavigate={() => setDrawerOpen(false)} />
      </Drawer>
    </Box>
  );
};

export default Layout;
